import { useState, type ReactNode } from 'react';
import { useFormFactor } from '@/lib/formFactor';
import { useSession } from '@/stores/session';
import { Tag } from '@/components/Tag';
import { Empty, ErrorState, Skeleton } from '@/components/States';
import { dateTime, qty } from '@/lib/format';
import { Chips, DataTable, DeskHead, MoreHint, Pager, PhoneBar, ReadonlyNote, type Col } from './kit/Kit';
import { useApi, useAutoMore, usePaged, useUrlState } from './kit/hooks';
import { importMode, importStatus, importType } from './status';
import type { ImportJob } from './types';
import { ImportJobDetailDialog } from './ImportJobDetailDialog';
import s from './ImportsPage.module.css';

/** 成功 / 失败比例条：状态色 + 文字，不只靠颜色（dataviz：状态色只给好 / 坏） */
function Ratio({ j }: { j: ImportJob }) {
  const total = Math.max(j.totalRows, j.successRows + j.failedRows, 1);
  const ok = (j.successRows / total) * 100;
  const bad = (j.failedRows / total) * 100;
  return (
    <div className={s.ratio} role="img" aria-label={`成功 ${j.successRows} 行，失败 ${j.failedRows} 行`}>
      {ok > 0 && <i className={s.okBar} style={{ width: `${ok}%` }} />}
      {bad > 0 && <i className={s.badBar} style={{ width: `${bad}%` }} />}
    </div>
  );
}

function ErrorsLink({ j, compact, fallback = null }: { j: ImportJob; compact?: boolean; fallback?: ReactNode }) {
  const can = useSession((st) => st.can);
  if (j.type === 'CARRIER_COMPANY') return <>{fallback}</>;
  if (!j._count?.errors || !can('imports.errors.download')) return <>{fallback}</>;
  // GET 下载，直接走老系统同一地址（不经 v2 请求层）
  return <a className={s.dl} href={`/api/v2/import/jobs/${j.id}/errors-csv`}>{compact ? '下载失败行' : `下载失败行 CSV（${j._count.errors}）`}</a>;
}

/**
 * 导入导出历史（system-*.html Y6）。一次导入一张卡：类型 + 状态 / 文件 / 总行·成功·失败 / 比例条。
 * 失败行可在本地修正并下载 CSV；重新导入仍需要业务导入流程确认。
 */
export function ImportsPage() {
  const ff = useFormFactor();
  const can = useSession((st) => st.can);
  const [selected, setSelected] = useState<ImportJob | null>(null);
  const [sp, set] = useUrlState();
  const type = sp.get('type') || 'all';
  const status = sp.get('status') || 'all';
  const phone = ff === 'phone';
  const legacyVisible = type !== 'CARRIER_COMPANY';
  const queryStatus = phone && status !== 'all' ? status : undefined;
  const list = usePaged<ImportJob>(can('imports.read') && legacyVisible ? '/api/import/jobs' : null, { type: type === 'all' ? undefined : type, status: queryStatus }, 20, phone);
  const allJobs = usePaged<ImportJob>(can('imports.read') ? '/api/import/jobs' : null, {}, 1, false);
  const stockJobs = usePaged<ImportJob>(can('imports.read') ? '/api/import/jobs' : null, { type: 'INITIAL_STOCK' }, 1, false);
  const exportJobs = usePaged<ImportJob>(can('imports.read') ? '/api/import/jobs' : null, { type: 'DATA_EXPORT' }, 1, false);
  const partialJobs = usePaged<ImportJob>(can('imports.read') ? '/api/import/jobs' : null, { status: 'PARTIAL' }, 1, false);
  const failedJobs = usePaged<ImportJob>(can('imports.read') ? '/api/import/jobs' : null, { status: 'FAILED' }, 1, false);
  const carriers = useApi<ImportJob[]>(can('imports.read') ? '/api/v2/import/carriers' : null);
  const carrierRows = (type === 'all' || type === 'CARRIER_COMPANY' ? carriers.data || [] : []).filter((job) => status === 'all' || job.status === status);
  const sentinel = useAutoMore(phone, list.loadMore);

  if (!can('imports.read')) return <Empty icon="lock" title="没有查看导入历史的权限">需要「查看导入导出历史」权限，请联系管理员。</Empty>;

  const carrierCount = carriers.data?.length || 0;
  const allCount = allJobs.total + carrierCount;
  const typeChips = <Chips value={type} onChange={(k) => set({ type: k === 'all' ? undefined : k })} items={[{ key: 'all', label: '全部', count: allCount }, { key: 'INITIAL_STOCK', label: '期初库存', count: stockJobs.total }, { key: 'CARRIER_COMPANY', label: '物流公司', count: carrierCount }, { key: 'DATA_EXPORT', label: '数据导出', count: exportJobs.total }]} />;
  const statusChips = <Chips value={status} onChange={(k) => set({ status: k === 'all' ? undefined : k })} items={[{ key: 'all', label: '全部状态' }, { key: 'SUCCESS', label: '成功' }, { key: 'PARTIAL', label: '部分失败' }, { key: 'FAILED', label: '失败' }]} />;
  const filtered = type !== 'all' || status !== 'all';
  const body = (() => {
    if (list.error && !list.items.length) return <ErrorState title="导入历史加载失败" message={list.error.message} detail="GET /api/import/jobs" onRetry={list.reload} />;
    if (list.loading && !list.items.length) return <div className={s.pad}><Skeleton /><Skeleton lines={3} /></div>;
    if (!list.items.length && carrierRows.length) return null;
    if (!list.items.length)
      return (
        <Empty icon="up" title={filtered ? '没有符合条件的记录' : '还没有导入或导出记录'}>
          {filtered ? '换个类型或状态再看看。' : '完成期初库存导入后，每次都会在这里留下一条批次记录。'}
        </Empty>
      );
    return null;
  })();
  const note = '导入会写库存流水，可在「出入库流水」按「期初导入」筛出来复核。';
  const carrierHistory = carrierRows.length ? <section className={s.carrierHistory}><h2>物流公司导入 <small>最近 {carrierRows.length} 次</small></h2>{carrierRows.map((job) => <article key={job.id}><span className={s.carrierWhen}>{dateTime(job.createdAt)}</span><b>{job.filename}</b><Tag tone={importStatus(job.type, job.status).tone} sm>{importStatus(job.type, job.status).label}</Tag><span>成功 {job.successRows} · 失败 {job.failedRows} · {importMode(job.mode)}</span>{can('imports.errors.download') && <button type="button" onClick={() => setSelected(job)}>查看{job.failedRows ? ' / 修正' : ''}</button>}</article>)}</section> : null;

  /* ------------------------------ 手机：一次一张卡 ------------------------------ */
  if (phone) {
    return (
      <div className={s.phone}>
        <PhoneBar large back="/settings" backLabel="设置" title="导入导出历史" sub={list.total || carrierRows.length ? `当前显示 ${list.items.length + carrierRows.length} 次` : undefined} />
        <div className={s.pchips}>{typeChips}{statusChips}</div>
        <div className={s.pbody}>
          {carrierHistory}
          {type === 'CARRIER_COMPANY' ? (carriers.error ? <ErrorState title="物流公司导入历史加载失败" message={carriers.error.message} onRetry={carriers.reload} /> : carriers.loading && !carriers.data ? <Skeleton /> : !carrierRows.length ? <Empty icon="up" title="没有符合条件的物流公司导入记录" /> : null) : body ||
            list.items.map((j) => {
              const st = importStatus(j.type, j.status);
              const mode = j.type === 'INITIAL_STOCK' ? importMode(j.mode) : null;
              return (
                <article key={j.id} className={s.card}>
                  <header><b>{importType[j.type] || j.type}</b>{mode && <small>{mode}</small>}<Tag tone={st.tone} sm>{st.label}</Tag></header>
                  <p className={s.file}>{j.filename || <span className="em">未记录文件名</span>}</p>
                  <div className={s.nums}>
                    <span><i>总行</i><b className="tnum">{qty(j.totalRows)}</b></span>
                    <span><i>成功</i><b className="tnum">{qty(j.successRows)}</b></span>
                    <span><i>失败</i><b className={`tnum ${j.failedRows ? 'neg' : ''}`}>{qty(j.failedRows)}</b></span>
                  </div>
                  {j.type === 'INITIAL_STOCK' && <Ratio j={j} />}
                  <footer><span className="tnum">{dateTime(j.createdAt)} · {j.createdBy?.name || '系统'}</span>{can('imports.errors.download') && <button type="button" className={s.detailButton} onClick={() => setSelected(j)}>查看{j.failedRows ? ' / 修正' : ''}</button>}<ErrorsLink j={j} compact /></footer>
                </article>
              );
            })}
          {legacyVisible && <MoreHint hasNext={list.hasNext} loading={list.loading} count={list.items.length} sentinel={sentinel} />}
          <ReadonlyNote>{note}</ReadonlyNote>
        </div>
        {selected && <ImportJobDetailDialog job={selected} onClose={() => setSelected(null)} onRetried={() => { list.reload(); carriers.reload(); }} />}
      </div>
    );
  }

  /* ------------------------------ iPad / 桌面：表格 ------------------------------ */
  const cols: Col<ImportJob>[] = [
    { key: 'id', title: '批次号', w: 170, render: (j) => <span className={`${s.batchId} tnum`} title={j.id}>{j.id}</span> },
    { key: 'type', title: '类型', w: 130, render: (j) => importType[j.type] || j.type },
    { key: 'st', title: '状态', w: 110, render: (j) => { const st = importStatus(j.type, j.status); return <Tag tone={st.tone} sm>{st.label}</Tag>; } },
    { key: 'file', title: '文件', w: 180, render: (j) => (j.filename ? <span title={j.filename}>{j.filename}</span> : <span className="em">—</span>) },
    { key: 'total', title: '总行', w: 96, align: 'r', render: (j) => qty(j.totalRows) },
    { key: 'ok', title: '成功', w: 96, align: 'r', render: (j) => <span className={s.success}>{qty(j.successRows)}</span> },
    { key: 'bad', title: '失败', w: 96, align: 'r', render: (j) => <span className={j.failedRows ? 'neg' : 'em'}>{qty(j.failedRows)}</span> },
    { key: 'dl', title: '操作', w: 150, align: 'c', render: (j) => <span className={s.actions}>{can('imports.errors.download') && <button type="button" className={s.detailButton} onClick={() => setSelected(j)}>查看</button>}<ErrorsLink j={j} compact fallback={<span className="em">—</span>} /></span> },
  ];
  return (
    <div className={s.desk}>
      <DeskHead title="导入导出历史" sub={<>{'每次导入都有批次号，失败行可以单独下载修正后重传'} <span className={s.count}>共 {allCount} 次 · {partialJobs.total + failedJobs.total} 次有失败行</span></>} className={s.resourceWideHead}>
        <a className={s.template} href={'/api/v2/import/initial-stock'} download="initial-stock-template.csv">下载导入模板</a>
      </DeskHead>
      <div className={s.listCard}>
        <div className={s.dchips}>{typeChips}</div>
        {type === 'CARRIER_COMPANY' ? (carriers.error ? <div className={s.dbody}><ErrorState title="物流公司导入历史加载失败" message={carriers.error.message} onRetry={carriers.reload} /></div> : carriers.loading && !carriers.data ? <div className={s.dbody}><Skeleton /></div> : !carrierRows.length ? <div className={s.dbody}><Empty icon="up" title="没有符合条件的物流公司导入记录" /></div> : <DataTable cols={cols} rows={carrierRows} rowKey={(j) => j.id} minWidth={1060} />) : body ? <div className={s.dbody}>{body}</div> : list.items.length ? <DataTable cols={cols} rows={list.items} rowKey={(j) => j.id} minWidth={1060} /> : <div className={s.dbody}><Empty icon="up" title={filtered ? '没有符合条件的记录' : '还没有导入或导出记录'}>{filtered ? '换个类型再看看。' : '在这里查看库存、物流公司导入和数据导出批次。'}</Empty></div>}
        {legacyVisible && !body && type !== 'CARRIER_COMPANY' && list.items.length > 0 && <Pager page={list.page} pageSize={list.pageSize} count={list.items.length} total={list.total} hasNext={list.hasNext} onGoto={list.goto} />}
        <p className={s.dnote}>{note}</p>
      </div>
      {selected && <ImportJobDetailDialog job={selected} onClose={() => setSelected(null)} onRetried={() => { list.reload(); carriers.reload(); }} />}
    </div>
  );
}
